import { Map } from "immutable";

import CompleteSessionPayload from "seneca-common/features/session/models/CompleteSessionPayload";
import SessionFinishedEvent from "seneca-common/features/session/models/SessionFinishedEvent";

import actionTypes from "../../../../action-types";
import { CourseStat } from "../../../../models/state/courseStats";
import transformServerSessionDuration from "../../../../utils/transformServerSessionDuration";
import transformServerTimestamp from "../../../../utils/transformServerTimestamp";

type CourseStats = Map<string, CourseStat>;

export const courseStats = (
  courseStats: CourseStats = Map<string, CourseStat>(),
  action: Record<string, any>
): CourseStats => {
  switch (action.type) {
    case actionTypes.COMPLETE_SESSION:
      return courseStats.update(action.payload.courseId, meta =>
        individualCourseStat(meta, action)
      );

    case actionTypes.COMPLETE_NOTES_ONLY_SESSION: {
      return courseStats.update(action.payload.courseId, meta =>
        individualCourseStat(meta, action)
      );
    }

    case actionTypes.RECEIVE_COURSE_STUDY_STATS:
      return courseStats.withMutations(courseStats => {
        return action.payload.courseStudyStats.stats.reduce(
          (newStats: any, stat: any) => {
            return newStats.set(stat.courseId, transformServerCourseStat(stat));
          },
          courseStats
        );
      });

    case actionTypes.RECEIVE_ALL_USER_COURSES_STATS:
      return courseStats.withMutations(courseStats => {
        return action.payload.reduce((newStats: any, stat: any) => {
          return newStats.set(stat.courseId, transformServerCourseStat(stat));
        }, courseStats);
      });

    case actionTypes.RECEIVE_RECONCILED_COURSE_STUDY_STATS: {
      return courseStats.withMutations(courseStats => {
        return action.payload.courseStudyStats.stats.reduce(
          (newStats: any, stat: any) => {
            return newStats.set(stat.courseId, new CourseStat(stat));
          },
          courseStats
        );
      });
    }

    default:
      return courseStats;
  }
};

export const individualCourseStat = (
  courseStat: CourseStat = new CourseStat(),
  action: Record<string, any>
): CourseStat => {
  switch (action.type) {
    case actionTypes.COMPLETE_SESSION:
      return applyCompleteSessionEventToCourseStat(courseStat, action.payload);

    case actionTypes.COMPLETE_NOTES_ONLY_SESSION:
      return applyNotesOnlySessionToCourseStat(courseStat, action.payload);

    default:
      return courseStat;
  }
};

export const applyCompleteSessionEventToCourseStat = (
  courseStat: CourseStat,
  {
    courseId,
    timeFinished,
    modulesStudied,
    endingCourseProficiency,
    endingCourseScore,
    totalModulesStudyTime
  }: SessionFinishedEvent
) => {
  const { sessionsCompleted } = courseStat;
  return courseStat.withMutations(courseStat =>
    courseStat
      // @ts-ignore
      .set("courseId", courseId)
      .set("sessionsCompleted", sessionsCompleted + 1)
      .update(
        "totalStudyTime",
        totalStudyTime => totalStudyTime + totalModulesStudyTime
      )
      // @ts-ignore
      .set("lastStudied", timeFinished)
      .update("sessionModulesStudied", studied => studied + modulesStudied)
      // @ts-ignore
      .set("proficiency", endingCourseProficiency)
      // @ts-ignore
      .set("score", endingCourseScore)
  );
};

const applyNotesOnlySessionToCourseStat = (
  courseStat: CourseStat,
  { courseId, timeStarted, timeFinished }: CompleteSessionPayload
) => {
  return courseStat.withMutations(courseStat =>
    courseStat
      // @ts-ignore
      .set("courseId", courseId)
      .set(
        "numberOfNotesOnlySessions",
        courseStat.numberOfNotesOnlySessions + 1
      )
      // @ts-ignore
      .set("lastStudied", timeFinished)
  );
};

export const transformServerCourseStat = (
  serverCourseStat: Record<string, any>
) =>
  new CourseStat({
    ...serverCourseStat,
    totalStudyTime: transformServerSessionDuration(
      serverCourseStat.totalStudyTime
    ),
    // @ts-ignore
    lastStudied: transformServerTimestamp(serverCourseStat.lastStudied)
  });
